import { useEffect, useId, useRef, useState } from 'react';
import { Check, MapPin } from 'lucide-react';
import { FloatingLabelInput } from './floating-label-input';
import { cn } from './utils';
import './municipality-picker.css';

/**
 * Result row shape consumed by the picker. The search hook lives in the
 * consuming app (it depends on a domain-specific data source) and is
 * injected via the `useSearch` prop — keeps this primitive decoupled
 * from any particular backend or query stack.
 */
export interface MunicipalityResult {
  name: string;
  cadastralCode: string;
  provinceCode: string;
}

/**
 * MunicipalityPicker — autocomplete on the canonical ISTAT comuni list.
 *
 * Visual contract:
 *   - Trigger is a `FloatingLabelInput` so the cell sits flush with the
 *     other fields in the parent form (same height/radius/bg).
 *   - When focused AND the typed query has ≥ 2 chars, an absolute-
 *     positioned dropdown surfaces below the input with up to 10
 *     matches. Each row shows "Nome (PR)". Active selection (the
 *     value already linked to a cadastral code) gets a leading check.
 *   - Pattern B surface — same as DropdownMenu / SelectField (solid
 *     `--color-bg` base + translucent `--color-surface-elevated`
 *     veneer) — see `municipality-picker.css`.
 *
 * Free-text fallback (mandatory):
 *   - Manual typing fires `onChange` with `cadastralCode: null`. The
 *     parent stores the human-readable name as `place_of_birth` and
 *     does NOT touch the cadastral column.
 *   - Selecting a result fires `onChange` with both `name` AND the
 *     ISTAT `cadastralCode` + `provinceCode`. The parent persists
 *     both atoms.
 *   - Required by project policy: foreign comuni / dirty imports /
 *     historical entries cannot be forced into the ISTAT list.
 *
 * Closing the dropdown:
 *   - Click outside the wrapper.
 *   - Escape key.
 *   - Pick a result.
 *   - Blurring the input alone is INSUFFICIENT (the user can be
 *     mid-click on a result row when blur fires); the document-level
 *     pointerdown listener handles the outside-click case.
 */

export interface MunicipalityValue {
  /**
   * Free-text comune name. Source of truth for display. May be a
   * curated ISTAT name OR an arbitrary string the user typed (foreign
   * comune, historical entry, dirty import).
   */
  name: string;
  /**
   * ISTAT 4-char cadastral code. Populated only when the user picked
   * the comune from the dropdown. `null` for manual typing or empty.
   */
  cadastralCode: string | null;
  /**
   * 2-letter province code. Populated alongside `cadastralCode` when
   * the user picks from the dropdown; `null` for manual typing.
   */
  provinceCode: string | null;
}

export interface MunicipalityPickerProps {
  /** Floating-label text — e.g. "Comune di nascita". */
  label: string;
  value: MunicipalityValue;
  onChange: (next: MunicipalityValue) => void;
  /**
   * Search hook injected by the consuming app. The picker keeps no
   * notion of where results come from — the host app supplies a hook
   * that takes the typed query and returns a results array + loading
   * flag. Lets a single primitive serve different domains / data
   * sources without coupling to any of them.
   */
  useSearch: (query: string) => {
    results: ReadonlyArray<MunicipalityResult>;
    isLoading: boolean;
  };
  /** Disable the input (e.g. while parent form is saving). */
  disabled?: boolean;
  className?: string;
  maxLength?: number;
}

export function MunicipalityPicker({
  label,
  value,
  onChange,
  useSearch,
  disabled,
  className,
  maxLength = 120,
}: MunicipalityPickerProps): JSX.Element {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const listboxId = useId();

  const { results, isLoading } = useSearch(value.name);
  const showDropdown = open && value.name.trim().length >= 2;

  // Outside-click + Escape close the dropdown. Pointerdown fires before
  // blur, which lets a click on a result row reach its handler before
  // the dropdown unmounts.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent): void => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handlePick = (m: MunicipalityResult): void => {
    onChange({
      name: m.name,
      cadastralCode: m.cadastralCode,
      provinceCode: m.provinceCode,
    });
    setOpen(false);
  };

  const handleType = (next: string): void => {
    // Manual typing breaks any prior link to a cadastral code — the
    // parent form persists the new free-text name with a NULL code.
    onChange({ name: next, cadastralCode: null, provinceCode: null });
    if (next.trim().length >= 2) setOpen(true);
  };

  return (
    <div
      ref={wrapperRef}
      className={cn('relative', className)}
      data-municipality-picker
    >
      <FloatingLabelInput
        label={label}
        value={value.name}
        onChange={(e) => handleType(e.target.value)}
        onFocus={() => {
          if (value.name.trim().length >= 2) setOpen(true);
        }}
        maxLength={maxLength}
        disabled={disabled}
        autoComplete="off"
        role="combobox"
        aria-expanded={showDropdown}
        aria-controls={listboxId}
      />

      {showDropdown ? (
        <div
          data-municipality-picker-content
          role="listbox"
          id={listboxId}
          /* Fixed h-72 (was max-h-72) so il dropdown non si ridimensiona
             con il numero di risultati: 1 risultato → resto dello spazio
             vuoto sotto, 50 risultati → la lista scorre. Stabile mentre
             l'utente digita; coerente con il contratto di altezza fissa
             dei picker modali (CountryPicker, PhoneCountryPicker,
             AddressAutocomplete: h-[32rem]). */
          className="absolute left-0 right-0 top-full z-popover mt-1 h-72 overflow-y-auto rounded-md p-1 text-sm text-app-text shadow-glass backdrop-blur-2xl"
        >
          {isLoading ? (
            <div className="px-3 py-2 text-xs text-muted-foreground">
              Cerco…
            </div>
          ) : results.length === 0 ? (
            <div className="px-3 py-2 text-xs text-muted-foreground">
              Nessun comune trovato. Puoi comunque digitare a mano.
            </div>
          ) : (
            results.map((m) => {
              const active = value.cadastralCode === m.cadastralCode;
              return (
                <button
                  key={m.cadastralCode}
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => handlePick(m)}
                  className={cn(
                    'flex w-full cursor-default select-none items-center gap-2 rounded-md px-3 py-2 text-left outline-none transition-colors hover:bg-foreground/10 focus:bg-foreground/10',
                    active ? 'text-foreground' : 'text-muted-foreground',
                  )}
                >
                  <MapPin
                    className="h-3.5 w-3.5 flex-none text-muted-foreground"
                    aria-hidden
                  />
                  <span className="flex-1 truncate text-foreground">
                    {m.name}
                  </span>
                  <span className="font-mono text-xs text-muted-foreground">
                    {m.provinceCode}
                  </span>
                  {active ? (
                    <Check
                      className="h-3.5 w-3.5 flex-none text-foreground"
                      aria-hidden
                    />
                  ) : null}
                </button>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
}
